import { Controller } from "@hotwired/stimulus"

// Guided keyboard tour for the portfolio gallery.
//
// While the gallery section is in view (or any time after the user has
// first scrolled into it), these keys are honoured globally:
//
//   ← / ↑ / k   → previous portfolio
//   → / ↓ / j   → next portfolio
//   Home        → first portfolio
//   End         → last portfolio
//   1 .. 6      → jump directly to portfolio N
//   T           → toggle the floating tour-hint badge
//   Esc         → exit (scroll past the gallery section)
//
// The tour uses Lenis to scroll smoothly to the target position. Target
// scroll = host.offsetTop + (host.offsetHeight - viewportH) * (N / (count-1))
// — which matches the gallery scene's sticky-aware progress so the device N
// is genuinely centred under the camera at the end of the tween.
//
// Markup contract: this controller is attached to the same element that
// hosts the gallery (data-scene="gallery") AND a hint <kbd> element marked
// with data-portfolio-tour-target="hint".

const COUNT = 6

export default class extends Controller {
  static targets = ["hint"]
  static values  = { activeIndex: { type: Number, default: 0 } }

  connect() {
    this._activeIdx = 0
    this._onKey = this._onKey.bind(this)
    this._onActive = this._onActive.bind(this)

    document.addEventListener("keydown", this._onKey)
    // Track the active portfolio mirrored from the scene's events so the
    // hint reflects the actual centred device, even when the user scrolls
    // manually rather than using the keyboard.
    this.element.addEventListener("portfolio:active", this._onActive)

    // First-time scroll into the section reveals the hint for a beat,
    // then it fades to a corner pill.
    this._intro = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !this._seen) {
          this._seen = true
          this._revealHint()
        }
      })
    }, { threshold: 0.35 })
    this._intro.observe(this.element)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
    this.element.removeEventListener("portfolio:active", this._onActive)
    if (this._intro) this._intro.disconnect()
    if (this._hideTimer) clearTimeout(this._hideTimer)
  }

  _onActive(event) {
    this._activeIdx = event.detail.index
    if (this.hasHintTarget) {
      this.hintTarget.dataset.activeIndex = String(this._activeIdx)
    }
  }

  _onKey(event) {
    // Only honour keys when the gallery section is at least partially in view.
    const rect = this.element.getBoundingClientRect()
    const vh = window.innerHeight
    const inView = rect.bottom > 0 && rect.top < vh
    if (!inView) return

    // Don't hijack typing in inputs/textareas.
    const tag = event.target.tagName?.toLowerCase()
    if (tag === "input" || tag === "textarea" || event.target.isContentEditable) return

    let handled = true
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowUp":
      case "k":
        this._jumpTo(this._activeIdx - 1)
        break
      case "ArrowRight":
      case "ArrowDown":
      case "j":
        this._jumpTo(this._activeIdx + 1)
        break
      case "Home":
        this._jumpTo(0)
        break
      case "End":
        this._jumpTo(COUNT - 1)
        break
      case "1": case "2": case "3":
      case "4": case "5": case "6":
        this._jumpTo(parseInt(event.key, 10) - 1)
        break
      case "Escape":
        this._scrollPast()
        break
      case "t":
      case "T":
        this._toggleHint()
        break
      default:
        handled = false
    }
    if (handled) {
      event.preventDefault()
      this._showHint()
    }
  }

  _jumpTo(idx) {
    const target = Math.max(0, Math.min(COUNT - 1, idx))
    if (target === this._activeIdx) return
    const rect = this.element.getBoundingClientRect()
    const offsetTop = window.scrollY + rect.top
    const stickyRange = rect.height - window.innerHeight
    const ratio = target / (COUNT - 1)
    const targetY = offsetTop + stickyRange * ratio
    this._scrollTo(targetY)
    this._activeIdx = target
  }

  _scrollPast() {
    const rect = this.element.getBoundingClientRect()
    const target = window.scrollY + rect.bottom + 24
    this._scrollTo(target)
  }

  _scrollTo(y) {
    if (window.__lenis) {
      window.__lenis.scrollTo(y, { duration: 1.4, easing: (t) => 1 - Math.pow(1 - t, 3) })
    } else {
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }

  _revealHint() {
    if (!this.hasHintTarget) return
    this.hintTarget.classList.add("portfolio-tour__hint--visible", "portfolio-tour__hint--prominent")
    if (this._hideTimer) clearTimeout(this._hideTimer)
    this._hideTimer = setTimeout(() => {
      this.hintTarget.classList.remove("portfolio-tour__hint--prominent")
    }, 4200)
  }

  _showHint() {
    if (!this.hasHintTarget) return
    this.hintTarget.classList.add("portfolio-tour__hint--visible")
  }

  _toggleHint() {
    if (!this.hasHintTarget) return
    this.hintTarget.classList.toggle("portfolio-tour__hint--visible")
  }
}
